'use client';

import { useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock } from 'lucide-react';
import type { PathParamDef } from '@/lib/integrations/types';

interface PathParamEditorProps {
  path: string;
  params: Record<string, string>;
  onChange: (params: Record<string, string>) => void;
  pathParamDefs?: PathParamDef[];  // Endpoint-specific param definitions with defaults
}

// Extract {paramName} patterns from path
function extractPathParams(path: string): string[] {
  const matches = path.match(/\{([^}]+)\}/g) || [];
  return matches.map(m => m.slice(1, -1)); // Remove { and }
}

// Resolve path with parameter values
export function resolvePath(path: string, params: Record<string, string>): string {
  return path.replace(/\{([^}]+)\}/g, (match, key) => {
    return params[key] || match;
  });
}

// Get defaults map from param definitions
export function getParamDefaults(defs?: PathParamDef[]): Record<string, string> {
  if (!defs) return {};
  const defaults: Record<string, string> = {};
  for (const def of defs) {
    if (def.default) {
      defaults[def.name] = def.default;
    }
  }
  return defaults;
}

export function PathParamEditor({ path, params, onChange, pathParamDefs }: PathParamEditorProps) {
  const pathParams = useMemo(() => extractPathParams(path), [path]);
  const resolvedPath = useMemo(() => resolvePath(path, params), [path, params]);

  // Build a map of param definitions for quick lookup
  const defMap = useMemo(() => {
    const map: Record<string, PathParamDef> = {};
    if (pathParamDefs) {
      for (const def of pathParamDefs) {
        map[def.name] = def;
      }
    }
    return map;
  }, [pathParamDefs]);

  if (pathParams.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Path Parameters
      </Label>
      <div className="space-y-2">
        {pathParams.map(param => {
          const def = defMap[param];
          const hasDefault = !!def?.default;
          const value = params[param] || '';

          return (
            <div key={param} className="flex items-center gap-2">
              <Label htmlFor={param} className="w-32 text-sm font-mono text-muted-foreground flex items-center gap-1">
                {'{' + param + '}'}
                {hasDefault && <Lock className="h-3 w-3 text-green-500" aria-label="Auto-filled" />}
              </Label>
              <Input
                id={param}
                value={value}
                onChange={e => onChange({ ...params, [param]: e.target.value })}
                placeholder={def?.description || `Enter ${param}`}
                className={`font-mono text-sm ${hasDefault ? 'bg-green-50 border-green-200' : ''}`}
                readOnly={hasDefault}
              />
              {hasDefault && (
                <span className="text-xs text-green-600 whitespace-nowrap">Auto</span>
              )}
            </div>
          );
        })}
      </div>
      <div className="text-xs text-muted-foreground">
        <span className="font-medium">Preview:</span>{' '}
        <code className="bg-muted px-1.5 py-0.5 rounded font-mono">{resolvedPath}</code>
      </div>
    </div>
  );
}

export default PathParamEditor;
